import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import DownloadModal from '@/components/Modals/DownloadModal/DownloadModal';
import SearchInput from '@/components/SearchInput';

import Filters from './dependencies/Filters/Filters';
import DetailsModalParcel from './dependencies/modals/DetailsModal/DetailsModalParcel';
import { mlparcelColumns } from './mlparcel-columns';
import useMLParcel from './useMLParcel';

const MLParcel = () => {
  const hook = useMLParcel();
  const theme = useTheme();

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems="end"
      justifyContent="center"
      gap="1rem"
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: `${theme.spacing(4)} 0 0`,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          height="auto"
          sx={{
            padding: `0 ${theme.spacing(4)}`,
            flexDirection: { xxs: 'column', xl: 'row' },
            gap: { xxs: '1rem', xl: 0 },
            alignItems: 'center',
          }}
        >
          <SearchInput
            buttonText="Buscar"
            placeholder="Buscar por Orden, Paquete (Pack ID) o Envío de MercadoLibre"
            onChange={(e) => hook.handleSearchChange(e.target.value)}
            value={hook.searchValue}
            onClick={hook.handleSearch}
            handleKeyPress={(e) => {
              if (e.key === 'Enter') hook.handleSearch();
            }}
            sx={{ width: '100%' }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', xs: 'row' },
              gap: '1rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button variant="outlined" onClick={hook.clearFilters} size="small">
              Borrar filtros
            </Button>
            <Box
              display="flex"
              gap="0.5rem"
              alignItems="center"
              justifyContent="center"
              onClick={() => hook.setOpenFilters(true)}
              sx={{
                cursor: 'pointer',
                ':hover': { backgroundColor: theme.palette.primary.lighter },
                padding: '0.5rem',
                borderRadius: '1rem',
              }}
            >
              <FilterIcon color="primary" />
              <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                Filtros
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box width="100%" mt={2}>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '700px' }}>
              <AgaveDataGrid
                columns={mlparcelColumns({
                  handleCreateOrder: hook.handleDirectCreateOrder,
                  creatingOrderIds: hook.creatingOrderIds,
                  handleDownloadLabel: hook.handleDownloadLabel,
                })}
                rows={hook.orders.data || []}
                localeText={{ noRowsLabel: 'No hay órdenes disponibles' }}
                onCellClick={(params) => {
                  if (!['actions', '__check__'].includes(params.colDef.field)) {
                    hook.handleRowClick(params.row);
                  }
                }}
                rowCount={hook.orders.total}
                loading={hook.orders.loading}
                disableRowSelectionOnClick
                getRowId={(row) => row.orderId}
                hideFooterSelectedRowCount
                paginationModel={{
                  page: hook.pagination.filters.page as number,
                  pageSize: hook.pagination.filters.page_size as number,
                }}
                disableColumnMenu
                paginationMode="server"
                onPaginationModelChange={hook.pagination.onPaginationModelChange}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <GenericDrawer
        open={hook.openFilters}
        onClose={hook.onClose}
        title="Filtros"
        disableEscapeKeyDown
        iconTitle={<FilterIcon color="primary" />}
      >
        <Filters
          selectedFilters={hook.filters}
          onChange={hook.onChangeFilter}
          clearFilters={hook.clearFilters}
          applyFilters={hook.applyFilters}
        />
      </GenericDrawer>

      <DetailsModalParcel
        isOpen={hook.modals.details}
        onClose={() => hook.handleModal('details')}
        order={hook.selectedOrder}
        clientId={hook.clientId}
      />

      <DownloadModal
        isOpen={hook.modals.downloadLabel}
        onClose={() => hook.handleModal('downloadLabel')}
        trackingIds={hook.selectedTrackingIds}
        downloadLabels={hook.downloadLabels}
        amount
      />
    </Box>
  );
};

export default MLParcel;
